import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Upload, X, BookOpen } from 'lucide-react';
import JoditEditor from 'jodit-react';
import { toast } from 'react-toastify';
import styles from './AddEbook.module.css';
import { useCategories } from '../../../hooks/categories/useCategories';
import { useCreateEbook } from '../../../hooks/admin/useAdminEbooks';
import Loading from '../../../common/HousGlass Loader/Loading';
import useAdminProfileStore from '../../../store/admin/adminProfileStore';
import { useSeriesByType } from '../../../hooks/admin/useAdminSeries';

const AddEbook = () => {
  const profile = useAdminProfileStore((state) => state.profile);
  const dropdownRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    mobileImage: null,
    desktopImage: null,
    content: '',
    hasChapters: false,
    genreName: '',
    genreId: '',
    slug: '',
    language: 'english',
    hasSeries: false,
    seriesId: '',
    chapterNumber: 1,
    author_id: profile?.id,
    meta_description: '',
    meta_keywords: '',
    page_title: '',
    paid: true, // Added paid field with default true
  });

  const [mobileImagePreview, setMobileImagePreview] = useState('');
  const [desktopImagePreview, setDesktopImagePreview] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const editor = useRef(null);
  const { categories = [] } = useCategories('Ebook');
  const { createEbook, isLoading } = useCreateEbook();
  const { series = [] } = useSeriesByType('Ebook');

  const filteredSeries = series.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    setFormData(prev => ({
      ...prev,
      seriesId: item.id
    }));
    setSearchTerm(item.title);
    setDropdownOpen(false);
  };

  const joditConfig = useMemo(() => ({
    theme: 'dark',
    height: 600,
    enableDragAndDropFileToEditor: true,
    showXPathInStatusbar: false,
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'ul', 'ol', '|',
      'table', 'link', '|',
      'left', 'center', 'right', 'justify', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'fullsize'
    ],
    uploader: {
      insertImageAsBase64URI: true
    },
    colors: {
      background: '#222222',
      text: '#ffffff',
      defaultTextColor: '#ffffff',
      defaultBackgroundColor: '#222222'
    },
    style: {
      background: '#222222',
      color: '#ffffff'
    },
    statusbar: false,
    readonly: false,
    toolbarSticky: true,
    showPlaceholder: false,
    saveIndicator: false,
    maxHeight: 800,
    minHeight: 400,
    useSearch: false,
    spellcheck: false,
    editorCssClass: 'dark-theme',
  }), []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'genreId') {
      // Find the selected genre to get its name
      const selectedGenre = categories.find(genre => genre.id === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        genreName: selectedGenre ? selectedGenre.name : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'mobile') {
          setMobileImagePreview(reader.result);
          setFormData(prev => ({ ...prev, mobileImage: file }));
        } else {
          setDesktopImagePreview(reader.result);
          setFormData(prev => ({ ...prev, desktopImage: file }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type) => {
    if (type === 'mobile') {
      setMobileImagePreview('');
      setFormData(prev => ({ ...prev, mobileImage: null }));
    } else {
      setDesktopImagePreview('');
      setFormData(prev => ({ ...prev, desktopImage: null }));
    }
  };

  const validateForm = () => {
    if (formData.hasSeries) {
      if (!formData.seriesId) {
        toast.error('Please select a series');
        return false;
      }
      if (!formData.chapterNumber || formData.chapterNumber <= 0) {
        toast.error('Chapter number must be greater than 0');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Create a copy of formData to send to API
    const dataToSubmit = { ...formData };
    
    // Only include series data if hasSeries is true
    if (!dataToSubmit.hasSeries) {
      delete dataToSubmit.seriesId;
      delete dataToSubmit.chapterNumber;
    }
    
    await createEbook(dataToSubmit);
    // Navigate back to the previous page
  };

  if (isLoading) {
    return <div><Loading /></div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BookOpen className={styles.headerIcon} />
        <h1>Add New Ebook</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="slug">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="author">Author</label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="genreId">Genre</label>
          <select
            id="genreId"
            name="genreId"
            value={formData.genreId}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="">Select a genre</option>
            {categories.map(genre => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="garhwali">Garhwali</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="korean">Korean</option>
            <option value="arabic">Arabic</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="paid"
              checked={formData.paid}
              onChange={handleInputChange}
            />
            This is paid content
          </label>
        </div>

        {/* Series Options */}
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="hasSeries"
              checked={formData.hasSeries}
              onChange={handleInputChange}
            />
            This book is part of a series
          </label>
        </div>

        {formData.hasSeries && (
          <>
            <div className={styles.formGroup} ref={dropdownRef}>
              <label className={styles.label}>Select Series:</label>
              <div
                className={styles.searchableSelect}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <input
                  type="text"
                  placeholder="Search or select series..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setDropdownOpen(true);
                  }}
                  className={styles.input}
                />
                <span className={styles.dropdownArrow}>▼</span>
              </div>
              {dropdownOpen && (
                <ul className={styles.dropdownList}>
                  {filteredSeries.length > 0 ? (
                    filteredSeries.map((item) => (
                      <li
                        key={item.id}
                        className={styles.dropdownItem}
                        onClick={() => handleSelect(item)}
                      >
                        {item.title} ({item.language || 'Unknown language'})
                      </li>
                    ))
                  ) : (
                    <li className={styles.dropdownItem}>No matching series</li>
                  )}
                </ul>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="chapterNumber">Chapter Number</label>
              <input
                type="number"
                id="chapterNumber"
                name="chapterNumber"
                min="1"
                value={formData.chapterNumber}
                onChange={handleInputChange}
                required={formData.hasSeries}
                style={{width:'100%', paddingBottom:'8px', paddingTop:'8px', backgroundColor:'#1e1e1e', borderRadius:'5px', color:'#fff'}}
              />
            </div>
          </>
        )}

        <div className={styles.imageUploadSection}>
          <div className={styles.imageUpload}>
            <label>Mobile Cover Image</label>
            <div className={styles.uploadArea}>
              {mobileImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={mobileImagePreview} alt="Mobile cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage('mobile')}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="mobileImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'mobile')}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Mobile Cover</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.imageUpload}>
            <label>Desktop Cover Image</label>
            <div className={styles.uploadArea}>
              {desktopImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={desktopImagePreview} alt="Desktop cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage('desktop')}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="desktopImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'desktop')}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Desktop Cover</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="content">Content</label>
          <JoditEditor
            ref={editor}
            value={formData.content}
            config={joditConfig}
            onChange={newContent => setFormData(prev => ({ ...prev, content: newContent }))}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="page_title">Page Title</label>
          <input
            type="text"
            id="page_title"
            name="page_title"
            value={formData.page_title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="meta_description">Meta Description</label>
          <textarea
            id="meta_description"
            name="meta_description"
            value={formData.meta_description}
            onChange={handleInputChange}
            rows="2"
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="meta_keywords">Meta Keywords (comma separated)</label>
          <input
            type="text"
            id="meta_keywords"
            name="meta_keywords"
            value={formData.meta_keywords}
            onChange={handleInputChange}
            required
          />
        </div>

        <button type="submit" className={styles.submitButton}>
          Add Ebook
        </button>
      </form>
    </div>
  );
};

export default AddEbook;